import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ArrowLeft,
  CalendarBlank,
  CheckCircle,
  Eye,
  EyeSlash,
  IdentificationCard,
  LockKey,
  ShieldCheck,
  Sparkle,
} from "@phosphor-icons/react";

const passwordRules = [
  { id: "length", label: "Minimo de 8 caracteres", test: value => value.length >= 8 },
  { id: "number", label: "Pelo menos 1 numero", test: value => /\d/.test(value) },
  { id: "letter", label: "Pelo menos 1 letra", test: value => /[a-zA-Z]/.test(value) },
];

function onlyDigits(value) {
  return value.replace(/\D/g, "");
}

function formatCpf(value) {
  const digits = onlyDigits(value).slice(0, 11);
  return digits
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1-$2");
}

function formatError(detail) {
  if (Array.isArray(detail)) {
    return detail.map(item => item.msg || item.message || String(item)).join(" ");
  }
  if (detail && typeof detail === "object") {
    return detail.message || detail.msg || JSON.stringify(detail);
  }
  return detail ? String(detail) : "Nao foi possivel concluir. Tente novamente.";
}

export default function FirstAccessPage() {
  const { firstAccessValidate, firstAccessSetPassword, demoMode } = useAuth();
  const [step, setStep] = useState(1);
  const [cpf, setCpf] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [identifiedUser, setIdentifiedUser] = useState(null);
  const [firstAccessToken, setFirstAccessToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const ruleStatus = useMemo(
    () => passwordRules.map(rule => ({ ...rule, valid: rule.test(password) })),
    [password]
  );

  const canValidateIdentity = onlyDigits(cpf).length === 11 && Boolean(birthDate);
  const canCreatePassword =
    ruleStatus.every(rule => rule.valid) &&
    password === confirmPassword &&
    confirmPassword.length > 0;

  const handleIdentitySubmit = async (event) => {
    event.preventDefault();
    setMessage("");

    if (!canValidateIdentity) {
      setMessage("Informe CPF e data de nascimento para continuar.");
      return;
    }

    setLoading(true);
    try {
      const result = await firstAccessValidate({
        cpf: onlyDigits(cpf),
        birth_date: birthDate,
      });
      setFirstAccessToken(result.first_access_token);
      setIdentifiedUser({
        name: result.name,
        email: result.email,
      });
      setStep(2);
    } catch (err) {
      setMessage(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setMessage("");

    if (!canCreatePassword) {
      setMessage("Confira os requisitos da senha e a confirmacao.");
      return;
    }

    setLoading(true);
    try {
      await firstAccessSetPassword({
        first_access_token: firstAccessToken,
        password,
        confirm_password: confirmPassword,
      });
      setStep(3);
    } catch (err) {
      setMessage(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen overflow-hidden bg-[#f5f7fb] lg:grid-cols-[1.02fr_0.98fr]">
      <section className="relative hidden flex-col justify-between overflow-hidden bg-[#2346ff] p-12 text-white lg:flex">
        <div className="absolute inset-0">
          <div className="absolute inset-8 rounded-[34px] border border-white/10" />
          <div className="absolute left-12 top-28 h-px w-44 bg-white/15" />
          <div className="absolute bottom-24 right-16 h-px w-64 bg-white/15" />
        </div>

        <div className="relative">
          <img src="/schumann-logo-blue-bg.svg" alt="Schumann" className="h-auto w-[220px]" />
          <p className="mt-6 text-xs uppercase tracking-[0.28em] text-white/62">
            Intranet corporativa
          </p>
        </div>

        <div className="relative max-w-[620px]">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/16 bg-white/10 px-3.5 py-2 text-sm text-white/88">
            <Sparkle className="h-4 w-4 text-[#ffd724]" weight="regular" />
            Primeiro acesso seguro
          </div>

          <h1 className="font-heading text-[42px] font-bold leading-[1.08] tracking-normal xl:text-[54px]">
            Confirme sua identidade e crie sua senha
          </h1>

          <p className="mt-6 max-w-lg text-base leading-7 text-white/72">
            O acesso inicial usa CPF e data de nascimento cadastrados na base da empresa. Depois disso, a senha passa a ser obrigatoria.
          </p>
        </div>

        <p className="relative text-sm text-white/45">
          © {new Date().getFullYear()} Schumann. Todos os direitos reservados.
        </p>
      </section>

      <main className="flex min-h-screen items-center justify-center px-5 py-10">
        <div className="w-full max-w-[460px]">
          <div className="mb-7 flex justify-center lg:hidden">
            <img src="/schumann-logo.svg" alt="Schumann" className="h-12 w-auto" />
          </div>

          <div className="rounded-[26px] border border-slate-200/80 bg-white p-6 shadow-[0_18px_48px_rgba(15,23,42,0.08)] sm:p-8">
            <RouterLink
              to="/login"
              className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition hover:text-[#2346ff]"
            >
              <ArrowLeft className="h-4 w-4" weight="regular" />
              Voltar para login
            </RouterLink>

            <div className="mb-7">
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#eef1ff]">
                {step === 3 ? (
                  <CheckCircle className="h-6 w-6 text-[#2346ff]" weight="regular" />
                ) : (
                  <ShieldCheck className="h-6 w-6 text-[#2346ff]" weight="regular" />
                )}
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-950">
                {step === 1 && "Primeiro acesso"}
                {step === 2 && "Crie sua senha"}
                {step === 3 && "Senha criada"}
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                {step === 1 && "Informe CPF e data de nascimento para localizar seu cadastro autorizado."}
                {step === 2 && "Defina uma senha segura para usar nos proximos acessos."}
                {step === 3 && "Agora entre com seu e-mail corporativo e a senha criada. Se a troca obrigatoria estiver ativa, o sistema ira orientar o proximo passo."}
              </p>
            </div>

            <div className="mb-6 grid grid-cols-3 gap-2">
              {["Identidade", "Senha", "Concluir"].map((label, index) => {
                const active = step >= index + 1;
                return (
                  <div
                    key={label}
                    className={`h-2 rounded-full transition ${active ? "bg-[#2346ff]" : "bg-slate-200"}`}
                    aria-label={label}
                  />
                );
              })}
            </div>

            {message && (
              <Alert variant="destructive" className="mb-4 rounded-xl py-2">
                <AlertDescription className="text-sm">{message}</AlertDescription>
              </Alert>
            )}

            {step === 1 && (
              <form onSubmit={handleIdentitySubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="cpf" className="text-sm font-semibold text-slate-700">
                    CPF
                  </Label>
                  <div className="relative">
                    <IdentificationCard className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2346ff]" weight="regular" />
                    <Input
                      id="cpf"
                      inputMode="numeric"
                      value={cpf}
                      onChange={event => setCpf(formatCpf(event.target.value))}
                      placeholder="000.000.000-00"
                      className="h-12 rounded-xl border-slate-200 bg-slate-50 pl-10 focus:border-[#2346ff] focus:ring-2 focus:ring-[#2346ff]/20"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="birthDate" className="text-sm font-semibold text-slate-700">
                    Data de nascimento
                  </Label>
                  <div className="relative">
                    <CalendarBlank className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2346ff]" weight="regular" />
                    <Input
                      id="birthDate"
                      type="date"
                      value={birthDate}
                      onChange={event => setBirthDate(event.target.value)}
                      className="h-12 rounded-xl border-slate-200 bg-slate-50 pl-10 focus:border-[#2346ff] focus:ring-2 focus:ring-[#2346ff]/20"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="h-12 w-full rounded-xl bg-[#2346ff] font-semibold text-white shadow-[0_12px_26px_rgba(35,70,255,0.22)] transition hover:bg-[#1d3ace]"
                >
                  {loading ? "Validando..." : "Validar cadastro"}
                </Button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                {identifiedUser && (
                  <div className="rounded-2xl border border-[#2346ff]/15 bg-[#f6f8ff] px-4 py-3 text-sm leading-6 text-slate-600">
                    Cadastro localizado para <strong className="text-slate-900">{identifiedUser.name}</strong>
                    {identifiedUser.email ? <> ({identifiedUser.email})</> : null}.
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-semibold text-slate-700">
                    Nova senha
                  </Label>
                  <div className="relative">
                    <LockKey className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2346ff]" weight="regular" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={event => setPassword(event.target.value)}
                      placeholder="••••••••"
                      className="h-12 rounded-xl border-slate-200 bg-slate-50 pl-10 pr-11 focus:border-[#2346ff] focus:ring-2 focus:ring-[#2346ff]/20"
                    />
                    <button
                      type="button"
                      aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-lg text-slate-400 transition hover:bg-white hover:text-[#2346ff]"
                    >
                      {showPassword ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-semibold text-slate-700">
                    Confirmar senha
                  </Label>
                  <div className="relative">
                    <LockKey className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2346ff]" weight="regular" />
                    <Input
                      id="confirmPassword"
                      type={showConfirm ? "text" : "password"}
                      value={confirmPassword}
                      onChange={event => setConfirmPassword(event.target.value)}
                      placeholder="••••••••"
                      className="h-12 rounded-xl border-slate-200 bg-slate-50 pl-10 pr-11 focus:border-[#2346ff] focus:ring-2 focus:ring-[#2346ff]/20"
                    />
                    <button
                      type="button"
                      aria-label={showConfirm ? "Ocultar senha" : "Mostrar senha"}
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-lg text-slate-400 transition hover:bg-white hover:text-[#2346ff]"
                    >
                      {showConfirm ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="rounded-2xl bg-slate-50 p-3">
                  <p className="mb-2 text-xs font-bold text-slate-700">Requisitos da senha</p>
                  <div className="space-y-1.5">
                    {ruleStatus.map(rule => (
                      <div key={rule.id} className="flex items-center gap-2 text-xs text-slate-500">
                        <CheckCircle className={`h-4 w-4 ${rule.valid ? "text-emerald-500" : "text-slate-300"}`} weight="regular" />
                        {rule.label}
                      </div>
                    ))}
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <CheckCircle className={`h-4 w-4 ${password && password === confirmPassword ? "text-emerald-500" : "text-slate-300"}`} weight="regular" />
                      Senhas iguais
                    </div>
                  </div>
                </div>

                <div className="grid gap-2 sm:grid-cols-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setStep(1);
                      setFirstAccessToken("");
                      setIdentifiedUser(null);
                    }}
                    disabled={loading}
                    className="h-12 rounded-xl border-slate-200 text-slate-700"
                  >
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="h-12 rounded-xl bg-[#2346ff] font-semibold text-white shadow-[0_12px_26px_rgba(35,70,255,0.22)] transition hover:bg-[#1d3ace]"
                  >
                    {loading ? "Criando..." : "Criar senha"}
                  </Button>
                </div>
              </form>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-800">
                  Senha criada com sucesso{demoMode ? " em modo demonstracao" : ""}. Agora faca login com seu e-mail corporativo.
                </div>
                <Button asChild className="h-12 w-full rounded-xl bg-[#2346ff] font-semibold text-white hover:bg-[#1d3ace]">
                  <RouterLink to="/login">Ir para login</RouterLink>
                </Button>
              </div>
            )}

            <div className="mt-5 rounded-2xl bg-[#fff8d7] px-4 py-3 text-xs leading-5 text-slate-700">
              Por seguranca, CPF e data de nascimento servem apenas para validar o primeiro acesso. Eles nao substituem sua senha.
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
